import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { endOfDay, format as formatDate, startOfDay } from 'date-fns';
import { toast } from 'react-toastify'; //need ToastContainer for floating
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { uuidv4 } from '@awp/helpers/Utils';
import { AWPLocalDB } from '@components/Common/AWPLocalDB';
import AwpDropdown from '@components/Common/DropDown/AwpDropdown';
import { selectAvalancheTechnicians, selectInternalIngestionCandidateStatus } from '@awp/stores/dropdownsSlice';
import Table, { cellComponentExp } from '@awp/components/table';
import { Accordion, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { GetJson, PostJson } from '@awp/helpers/fetchReact';
import { EventTypes, FORM_MODE, formFailedStatus, OfflineFormStatus, ParentEventTypes, REST_API } from '@awp/constants';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { dateBuilder, endDateValidator, startDateValidator } from '@awp/components/Common/Helpers/AwpDateHelper';
import DatePicker from '@components/Common/AwpDatePicker';
import {
  getAllGeographicAreas,
  getAvalancheGeographicAreas,
  getAvalancheObservations,
  selectAllObservations,
} from '@awp/stores/avalancheSlice';
import {
  convLocalToIsoString,
  getLocationPrefix,
  getUtcToLocalByBreakpoints,
  getUtcToLocalByFormat,
  isValueEmpty,
  resizePointLocationName,
} from '@awp/helpers/utilsReact';
import SupportOffline from '@awp/components/SupportOffline';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectEventTypes, selectPhasedEventTypes } from '@awp/stores/eventSlice';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import { useAvalancheTableFilterContext } from './AvalancheTableFilterContext';
import UploadWithCheckbox from './UploadWithCheckbox';

const EventTable = ({
  title,
  breakpoints,
  setFullData,
  fullData,
  loading,
  setLoading,
  // startDate,
  // setStartDate,
  // endDate,
  // setEndDate,
  // selectedAvalanchTech,
  // setSelectedAvalancheTech,
  // selectedProgram,
  // setSelectedProgram,
  // selectedArea,
  // setSelectedArea,
  // selectedStatus,
  // setSelectedStatus,
  // selectedEventType,
  // setSelectedEventType,
  // search,
  // setSearch,
  setMode,
  eventId,
  setEventId,
  setEventTypeToActOn,
  eventTypeToCreate,
  setEventTypeToCreate,
  // index,
  // setIndex,
  sort,
  setSort,
  // defaultPageSize,
  // setDefaultPageSize,
  setIsDuplicated,
  createXnOccurrence,
  expandedRowEntityId,
  setExpandedRowEntityId,
  isAccordionExpanded,
  setIsAccordionExpanded,
}) => {
  const {
    startDate,
    endDate,
    selectedProgram,
    selectedArea,
    selectedStatus,
    selectedAvalanchTech,
    selectedEventType,
    search,
    scrollPosition,
    index,
    defaultPageSize,
    setEndDate,
    setStartDate,
    setSelectedProgram,
    setSelectedArea,
    setSelectedStatus,
    setSelectedAvalancheTech,
    setSelectedEventType,
    setSearch,
    setScrollPosition,
    setIndex,
    setDefaultPageSize,
  } = useAvalancheTableFilterContext();

  const internalIngestionCandidateStatus = useSelector(selectInternalIngestionCandidateStatus);
  const currentUser = useSelector(selectCurrentUser);
  const triggerTypes = useSelector((state) => getAvalancheObservations(state, 'aval_trig_type_cd'));
  const allObservations = useSelector((state) => selectAllObservations(state));
  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const allAvalancheAreas = useSelector((state) => getAllGeographicAreas(state));
  const eventTypes = useSelector(selectEventTypes);
  const parentEventTypes = eventTypes.filter((x) => ParentEventTypes.includes(x.value));
  const phasedEventTypes = useSelector(selectPhasedEventTypes);
  const [errors, setErrors] = useState(null);
  const [showYesNoModal, setShowYesNoModal] = useState(false);

  // need to filter based on selected control type and R & Y
  const explosiveTriggerTypes = triggerTypes.filter((t) => t.value.startsWith('X'));

  const EntityId = 'id';

  const hasSearch = (src) => {
    return src ? src.toString().toLowerCase().includes(search.toLowerCase()) : false;
  };

  useEffect(() => {
    applyUserPreferences();
    loadFullData();
  }, [startDate, endDate]);

  const applyUserPreferences = () => {
    const data = (JSON.parse(currentUser.Preferences) ?? {})?.list;

    if (!data) return;

    if (data.programs && isValueEmpty(selectedProgram)) {
      setSelectedProgram(avalanchePrograms.filter((x) => data.programs.includes(x.value)));
    }

    if (data.areas && isValueEmpty(selectedArea)) {
      setSelectedArea(avalancheAreas.filter((x) => data.areas.includes(x.value)));
    }

    if (data.events && isValueEmpty(selectedEventType)) {
      setSelectedEventType(eventTypes.filter((x) => data.events.includes(x.value)));
    }
  };

  const getObservations = (typeName) => {
    const filteredObservations = allObservations
      .filter(({ ObservationTypeName, IsActive }) => ObservationTypeName === typeName && IsActive)
      .sort((a, b) => {
        return a.DisplayOrder < b.DisplayOrder ? -1 : a.DisplayOrder > b.DisplayOrder ? 1 : 0;
      });

    return filteredObservations.map((obs) => {
      let obj = { label: obs.Value + ' - ' + obs.Meaning, value: obs.Value };

      return obj;
    });
  };

  const loadFullData = async () => {
    setLoading(true);
    const dateFrom = formatDate(startDate.value, 'yyyy-MM-dd');
    const dateTo = formatDate(endDate.value, 'yyyy-MM-dd');
    const url = `${REST_API.INGESTION_CANDIDATE}?dateFrom=${dateFrom}&dateTo=${dateTo}`;

    const onlineRecords = [];
    GetJson(url)
      .then((response) => response.json())
      .then(async (records) => {
        records.forEach((record) => {
          const candidate = JSON.parse(record.JsonObject);
          candidate.status = record.Status;
          candidate.internal.timestamp = new Date(candidate.internal.timestamp);
          candidate.internal.event_type = eventTypes?.find(
            (x) => x.value === record.event_type || x.value === candidate.event_type,
          );
          candidate.location = getLocation(JSON.parse(record.JsonObject));
          onlineRecords.push(candidate);
        });
        const offlineRecords = await AWPLocalDB.avalancheData.toArray();
        if (offlineRecords && offlineRecords.length > 0)
          offlineRecords.map((r) => {
            r.internal.event_type = eventTypes?.find((x) => x.value === r.event_type);
            r.location = getLocation(r);
            return r;
          });
        const data = [...offlineRecords, ...onlineRecords];
        //Re-org all avalanche records to three-level structure
        const _data = [];
        if (data && data.length > 0) {
          const eventMap = new Map();

          // Iterate through all events and add them to the map
          data.forEach((evt) => {
            const _evt = evt;
            _evt.JsonObject = JSON.stringify(evt);
            if (_evt.ChildIngestionCandidates == null || _evt.ChildIngestionCandidates.length === 0)
              _evt.ChildIngestionCandidates = []; // Add an empty array to hold children
            eventMap.set(evt.id, _evt);
          });

          // Iterate again to build the hierarchy
          data.forEach((event) => {
            const _event = event;
            _event.JsonObject = JSON.stringify(event);
            if (!event.parent_trace_id) {
              // This is a top-level event (grandparent), add it to top-level events
              _data.push(_event);
            } else {
              // This is a child event, find its parent and add it to the parent's children array
              const parentEvent = eventMap.get(event.parent_trace_id);
              if (parentEvent) {
                const index = parentEvent.ChildIngestionCandidates.findIndex((x) => x.id == _event.id);
                if (index > -1) {
                  parentEvent.ChildIngestionCandidates[index] = event;
                } else {
                  parentEvent.ChildIngestionCandidates.push(_event);
                }
              } else {
                //has parent id but parent object is not found. e.g. parent is deleted on the backend
                _data.push(_event);
              }
            }
          });
        }
        setFullData([..._data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
        setLoading(false);
      });
  };

  const getLocation = (row) => {
    const value = row.internal;

    if (row.event_type === EventTypes.AvalancheOccurrence) {
      return value.avalanche_path?.label || '';
    } else if (row.event_type === EventTypes.AvalancheControl) {
      return value.avalanche_path?.label || '';
    } else if (
      row.event_type === EventTypes.RoadClosure ||
      phasedEventTypes.find((x) => x.value === value?.event_type?.value)
    ) {
      if (value.segment && value.segment.value > 0) return value.segment.label;
      if (value.fromPoint && value.fromPoint.value > 0 && value.toPoint && value.toPoint.value > 0)
        return `${value.fromPoint.label} / ${value.toPoint.label}`;
      if (value.fromPoint && value.fromPoint.value > 0) return `${value.fromPoint.label} / - `;
      if (value.toPoint && value.toPoint.value > 0) return `- / ${value.toPoint.label}`;
      return '- / -';
    }

    return '';
  };

  const getFilteredArea = () => {
    const programIds = selectedProgram?.map((x) => x.value);

    if (programIds && programIds.length !== 0) {
      return avalancheAreas
        .filter((a) => programIds.includes(a.parentId))
        .sort((a, b) => a.lastUpdated - b.lastUpdated);
    }

    return avalancheAreas;
  };

  const getFilteredData = () => {
    const programIds = selectedProgram?.map((x) => x.value);
    const areaIds = selectedArea?.map((x) => x.value);
    const eventTypeIds = selectedEventType?.map((x) => x.value);
    const statusLabels = selectedStatus?.map((x) => x.label);
    const avTechIds = selectedAvalanchTech?.map((x) => x.value);
    const _fullData = JSON.parse(JSON.stringify(fullData));

    let filtered = [];

    _fullData.map((o) => {
      if (
        (getUtcToLocalByFormat(o.timestamp) >=
          getUtcToLocalByFormat(convLocalToIsoString(startOfDay(startDate.value))) &&
          getUtcToLocalByFormat(o.timestamp) <= getUtcToLocalByFormat(convLocalToIsoString(endOfDay(endDate.value)))) ||
        o.timestamp === undefined ||
        o.timestamp === null
      )
        filtered.push(o);
    });

    if (programIds && programIds.length !== 0) {
      filtered = filtered.filter((o) => {
        if (o) {
          const prgm = allAvalancheAreas.find((p) => p.value === o.internal.avalanche_area?.value);
          if (prgm) return programIds.includes(prgm?.programId);
        }
      });
    }

    if (areaIds && areaIds.length !== 0) {
      filtered = filtered.filter((o) => {
        return areaIds.includes(
          o?.EventTypeNm === 'AVALANCHE EVENT' ? o.internal.avalanche_area?.parentId : o.internal.avalanche_area?.value,
        );
      });
    }

    if (statusLabels && statusLabels.length !== 0) {
      filtered = filterEvent(filtered, statusLabels, 'status');
    }

    if (avTechIds && avTechIds.length !== 0) {
      filtered = filterEvent(filtered, avTechIds, 'submittedBy');
    }

    if (eventTypeIds && eventTypeIds.length !== 0) {
      filtered = filterEvent(filtered, eventTypeIds, 'event_type');
    }

    if (!isValueEmpty(search)) {
      filtered = filterEvent(filtered, search, 'search');
    }

    if (expandedRowEntityId) {
      filtered = filtered.map((row) => {
        if (expandedRowEntityId === row[EntityId]) {
          row.expanded = true;
        }

        return row;
      });
    }

    return filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const filterEvent = (filtered, selectedValues, field) => {
    filtered = filtered.filter((o) => {
      let childEvts = null;

      if (o.ChildIngestionCandidates && o.ChildIngestionCandidates.length > 0) {
        // recursively iterate over child events
        const filteredChildEvents = filterEvent(o.ChildIngestionCandidates, selectedValues, field);
        if (filteredChildEvents.length > 0) {
          childEvts = filteredChildEvents;
          o.ChildIngestionCandidates = [...filteredChildEvents];
        } else {
          // if none of the child events match the selected value, remove the child events
          o.ChildIngestionCandidates = null;
        }
      }
      // if the parent event matches the selected value or has child events that match the selected value, keep the parent event
      if (field === 'status') {
        return selectedValues.includes(o.status) || (childEvts && childEvts.length > 0);
      } else if (field === 'submittedBy') {
        return (
          (selectedValues.includes(o.userId) && (o.userType + '').toLowerCase() === 'idir') ||
          (childEvts && childEvts.length > 0)
        );
      } else if (field === 'event_type') {
        return selectedValues.includes(o.event_type) || (childEvts && childEvts.length > 0);
      } else if (field === 'search') {
        return (
          hasSearch(o.internal?.avalanche_program?.label) ||
          hasSearch(o.internal?.avalanche_program?.value) ||
          hasSearch(o.internal?.avalanche_area?.label) ||
          hasSearch(o.internal?.avalanche_area?.value) ||
          hasSearch(o.internal?.avalanche_path?.label) ||
          hasSearch(o.internal?.avalanche_path?.value) ||
          hasSearch(o.internal?.segment?.value) ||
          hasSearch(o.internal?.segment?.label) ||
          hasSearch(o.internal?.fromPoint?.value) ||
          hasSearch(o.internal?.fromPoint?.label) ||
          hasSearch(o.internal?.toPoint?.value) ||
          hasSearch(o.internal?.toPoint?.label) ||
          hasSearch(o.userName) ||
          hasSearch(o.status) ||
          (childEvts && childEvts.length > 0)
        );
      }
    });
    return filtered;
  };

  const deepCopyObject = (obj) => {
    const copy = {};
    for (const key in obj) {
      if (obj[key] && typeof obj[key] === 'object') {
        copy[key] = Array.isArray(obj[key]) ? deepCopyArray(obj[key]) : deepCopyObject(obj[key]);
      } else {
        copy[key] = obj[key];
      }
    }
    return copy;
  };

  const deepCopyArray = (array) => {
    return array.map((item) => {
      if (item && typeof item === 'object') {
        if (Array.isArray(item)) {
          return deepCopyArray(item);
        } else {
          return deepCopyObject(item);
        }
      }
      return item;
    });
  };

  const handleSubmitMain = async (record, parentOnly) => {
    const success = await handleSubmit(record.id);

    if (!success) {
      toast.error('Submission failed. Please try again.');
      return;
    }

    if (parentOnly) {
      loadFullData();
      toast.success('Successfully submitted');
      return;
    }

    let validChildren = record.ChildIngestionCandidates.filter((child) => child.status === OfflineFormStatus.Valid);

    if (validChildren.length === 0) {
      loadFullData();
      toast.success('Successfully submitted');
      return;
    }

    if (record.event_type === EventTypes.RoadClosure) {
      validChildren = validChildren
        .map((c) => ({
          ...c,
          order: phasedEventTypes.find((x) => x.value === c.event_type)?.order ?? 99, // Default order 99 if not found
        }))
        .sort((a, b) => a.order - b.order || new Date(a.timestamp) - new Date(b.timestamp));
    }

    let successCount = 0;

    for (const child of validChildren) {
      const childSuccess = await handleSubmit(child.id);
      if (childSuccess) {
        successCount++;
      } else {
        break;
      }
    }

    loadFullData();

    if (successCount === validChildren.length) {
      toast.success('Successfully submitted');
    } else {
      const failedCount = validChildren.length - successCount;
      toast.error(`${failedCount} child submission(s) failed. Please retry for the failed ones.`);
    }
  };

  const handleSubmit = async (id) => {
    if (!window.awpIsOnline || !navigator.onLine) {
      setErrors({
        'Service Unavailable': ['Please try again after reloading the page.'],
      });
      return false;
    }

    try {
      const url = REST_API.INGESTION_CANDIDATE;
      const entity = await AWPLocalDB.avalancheData.get(id);

      if (!entity) {
        console.error(`Error: Entity with id ${id} not found.`);
        return false;
      }

      Object.assign(entity, {
        userName: currentUser.UserName,
        userType: currentUser.UserType,
        userId: currentUser.SecurityIdentifierId,
        status: OfflineFormStatus.Submitted,
        lastUpdated: Date.now(),
        ChildIngestionCandidates: null,
        JsonObject: null,
      });

      const dto = { LocalId: id, StructuredDefinition: JSON.stringify([entity]) };

      const response = await PostJson(url, dto);

      if (!response.ok) {
        if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
        console.error(`Submission failed for id ${id} with status ${response.status}`);
        return false;
      }

      await AWPLocalDB.avalancheData.delete(id);

      return true;
    } catch (error) {
      console.error(`Error submitting id ${id}:`, error);
      return false;
    }
  };

  const checkChildOccrruences = (event) => {
    let cntChild = 0;
    if (isValueEmpty(event) || isValueEmpty(AWPLocalDB) || isValueEmpty(AWPLocalDB.avalancheData)) return cntChild;
    if (event.event_type === EventTypes.AvalancheControl || event.event_type === EventTypes.RoadClosure) {
      AWPLocalDB.avalancheData
        .filter((r) => {
          return (
            !isValueEmpty(r.parent_trace_id) &&
            (r.parent_trace_id + '').toLocaleLowerCase() === (event.id + '').toLocaleLowerCase()
          );
        })
        .toArray(async (data) => {
          cntChild = data.length;
          if (cntChild === 0) {
            setEventId(event.id);
            setShowYesNoModal(true);
          } else {
            let msg = `Cannot delete a parent Event with ${cntChild} existing child Occurrences.`;
            toast.error(msg, {
              autoClose: false,
            });
            return cntChild;
          }
        });
    } else {
      setEventId(event.id);
      setShowYesNoModal(true);
    }
    return cntChild;
  };

  const isValidToDuplicate = (event) => {
    const eventInvalid = checkProperties(event, event?.internal);
    const childInvalid = (event?.ChildIngestionCandidates || []).flatMap((child) =>
      checkProperties(child, child?.internal),
    );

    const allInvalidLabels = [...eventInvalid, ...childInvalid];
    const noDuplicates = new Set(allInvalidLabels);
    return {
      isValid: allInvalidLabels.length === 0,
      invalidLabels: Array.from(noDuplicates),
    };
  };

  const checkProperties = (target, internal = {}) => {
    const props = target?.properties || {};
    const invalidLabels = [];

    for (const [key, value] of Object.entries(props)) {
      if (!value || (typeof value === 'object' && Object.keys(value).length > 0)) continue;

      const validOptions = getObservations(key);
      if (!validOptions) continue;

      const isValid = validOptions.some((opt) => opt?.value === value);
      if (!isValid) {
        const internalLabel = internal[key]?.label || null;
        if (internalLabel != null) {
          invalidLabels.push(`Event is not valid for duplication: code table value '${internalLabel}' is not active`);
        }
      }
    }

    return invalidLabels;
  };

  const handleDelete = async () => {
    await AWPLocalDB.avalancheData.delete(eventId);
    setShowYesNoModal(false);
    loadFullData();
    toast.success('Successfully deleted');
  };
  const cleanEventBeforeDuplicate = (evt) => {
    evt.JsonObject = null;
    if (evt.expanded) delete evt.expanded;
    return evt;
  };
  const handleDuplicate = (event) => {
    const dupeId = uuidv4();

    let comment = '';
    if (event.comment) {
      comment += `${event.comment}`;
    }
    //event.JsonObject = null;
    let duplicatedEvent = {
      ...event,
      id: dupeId,
      trace_id: dupeId,
      status: 'Duplicated',
      comment: comment,
      lastUpdated: Date.now(),
      ChildIngestionCandidates: [],
    };
    cleanEventBeforeDuplicate(duplicatedEvent);
    // Duplicate and process children
    if (event.ChildIngestionCandidates && event.ChildIngestionCandidates.length > 0) {
      const children = deepCopyArray(event.ChildIngestionCandidates);

      children.map((child) => {
        const childDupeId = uuidv4();
        // // Parse the JSON object of the child
        // let parsedObject = JSON.parse(child.JsonObject);
        // parsedObject.comment = `Duplicated from ${getUtcToLocalByFormat(parsedObject.timestamp, 'yyyy-MM-dd HH:mm')}`;
        // parsedObject.status = 'Duplicated';
        // parsedObject.id = childDupeId;
        // parsedObject.trace_id = childDupeId;
        // parsedObject.parent_trace_id = dupeId;
        // parsedObject.lastUpdated = Date.now();
        // const parsedJsonObject = JSON.parse(parsedObject.JsonObject);
        // parsedJsonObject.comment = `Duplicated from ${getUtcToLocalByFormat(
        //   parsedJsonObject.timestamp,
        //   'yyyy-MM-dd HH:mm',
        // )}`;
        // parsedJsonObject.status = 'Duplicated';
        // parsedJsonObject.id = childDupeId;
        // parsedJsonObject.trace_id = childDupeId;
        // parsedJsonObject.parent_trace_id = dupeId;
        // parsedJsonObject.lastUpdated = Date.now();
        // parsedObject.JsonObject = JSON.stringify(parsedJsonObject);

        // Create the duplicated child object
        const duplicatedChild = {
          ...child,
          id: childDupeId,
          trace_id: childDupeId,
          status: 'Duplicated',
          parent_trace_id: dupeId,
          comment: '',
          lastUpdated: Date.now(),
        };
        cleanEventBeforeDuplicate(duplicatedChild);
        //duplicatedChild.JsonObject = JSON.stringify(parsedObject);
        AWPLocalDB.avalancheData.add(duplicatedChild).then(async () => {});
        return duplicatedChild;
      });
      // let parsedEvent = JSON.parse(duplicatedEvent.JsonObject);
      // parsedEvent.id = dupeId;
      // parsedEvent.trace_id = dupeId;
      // parsedEvent.status = 'Duplicated';
      // parsedEvent.comment = comment;
      // parsedEvent.lastUpdated = Date.now();
      // parsedEvent.ChildIngestionCandidates = duplicatedEvent.ChildIngestionCandidates;
      // let parsedEventChild = JSON.parse(parsedEvent.JsonObject);
      // parsedEventChild.id = dupeId;
      // parsedEventChild.trace_id = dupeId;
      // (parsedEventChild.lastUpdated = Date.now()), (parsedEvent.JsonObject = JSON.stringify(parsedEventChild));
      // duplicatedEvent.JsonObject = JSON.stringify(parsedEvent);
    }

    /*
    if (duplicatedOccurrence.timestamp) {
      const newDate = new Date();
      duplicatedOccurrence.internal.timestamp = newDate;
      duplicatedOccurrence.timestamp = convLocalToIsoString(newDate);
    }

    if (duplicatedOccurrence.properties?.aval_trig_type_cd) {
      delete duplicatedOccurrence.properties.aval_trig_type_cd;
      delete duplicatedOccurrence.internal.aval_trig_type_cd;
    }
    if (duplicatedOccurrence.uncertainty_hours) {
      delete duplicatedOccurrence.uncertainty_hours;
    }*/

    AWPLocalDB.avalancheData
      .add(duplicatedEvent)
      .then(async () => {
        toast.success('Event has been duplicated');
        setEventId(duplicatedEvent.id);
        setMode(FORM_MODE.EDIT);
        setIsDuplicated(true);
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      });
  };

  const handleAddPhasedEvent = (event, fullData) => {
    const id = uuidv4();

    //find existing phased events of the parent road close event
    const phasedEvents = fullData.filter(
      (x) =>
        x.userId === currentUser.SecurityIdentifierId &&
        phasedEventTypes.find((y) => y.value === x.event_type) && //phased event type
        x.rc_timestamp === event.timestamp && //road closure timestamp of the phased event equals to the timestamp of the road closure event
        ((event.road_closure_segment && x.road_closure_segment === event.road_closure_segment) || //location of the phased event is equals to the location of the road closure event
          (x.road_closure_points?.length === 2 &&
            event.road_closure_points?.length === 2 &&
            x.road_closure_points[0] === event.road_closure_points[0] &&
            x.road_closure_points[1] === event.road_closure_points[1])),
    );

    // Add exists flag to phasedEventTypes
    const filteredPhasedEventTypes = phasedEventTypes.filter(
      (x) => !event.ChildIngestionCandidates.some((y) => y.event_type == x.value),
    );
    const eventTypes = filteredPhasedEventTypes.map((x) => ({
      ...x,
      exists: phasedEvents?.some((y) => y.event_type === x.value),
    }));

    // Find the next phased event, initialize to the last phased event
    let eventType = eventTypes[eventTypes.length - 1];

    // If the last phased type exists, leave eventType as the last phased event
    if (!eventTypes[eventTypes.length - 1].exists) {
      // In order to handle the case where some phased events are skipped, use the reverse for-loop
      for (let i = eventTypes.length - 1; i >= 0; i--) {
        if (!eventTypes[i].exists && (i === 0 || eventTypes[i - 1].exists)) {
          eventType = eventTypes[i];
          break;
        }
      }
    }

    let comment = '';
    let phasedEvent = {
      id,
      provider: 'INTERNAL_INGESTION_' + currentUser.Env.toUpperCase(),
      trace_id: id,
      parent_trace_id: event.id,
      rc_timestamp: event.timestamp,
      timestamp: convLocalToIsoString(new Date()),
      event_type: eventType?.value,
      avalanche_area: event.avalanche_area,
      road_closure_segment: event.road_closure_segment,
      road_closure_points: event.road_closure_points,
      comment: comment,
      userName: currentUser.UserName,
      userType: currentUser.UserType,
      userId: currentUser.SecurityIdentifierId,
      status: OfflineFormStatus.Duplicated,
      internal: { ...event.internal, event_type: eventType },
      lastUpdated: Date.now(),
    };
    AWPLocalDB.avalancheData
      .add(phasedEvent)
      .then(async () => {
        toast.success('Phased event has been created');
        setEventId(phasedEvent.id);
        setEventTypeToActOn(eventType);
        setMode(FORM_MODE.EDIT);
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      });
  };

  const getGroupLabel = (groupChar) => {
    switch (groupChar) {
      case 'N':
        return 'Natural Triggers';
      case 'H':
        return 'Artificial Triggers, helicopters';
      case 'V':
        return 'Over-snow vehicles (other than snowmobile)';
      case 'S':
        return 'Artificial Triggers, skiers, etc';
      case 'M':
        return 'Artificial Triggers, snowmobiles';
      case 'O':
        return 'Artificial Triggers (miscellaneous)';
      case 'X':
        return 'Explosive Triggers';
      default:
        return 'Unknown Triggers';
    }
  };

  const groupTrigTypes = (trigTypes) => {
    let groupChar = '';
    const groups = [];
    let group;

    trigTypes.forEach((x, index) => {
      if (groupChar !== x.value.charAt(0)) {
        groupChar = x.value.charAt(0);
        group = { label: getGroupLabel(groupChar), index, options: [] };
        groups.push(group);
      }
      group.options.push(x);
    });
    return groups;
  };

  const filterExplosiveTriggerTypes = (aval_cntrl_type_cd) => {
    return explosiveTriggerTypes.filter(
      (x) => x.value === 'Xy' || x.value === 'Xr' || x.value === `X${aval_cntrl_type_cd?.value}`,
    );
  };

  const handleAddXnOccurrence = (event) => {
    const eventType = eventTypes.find((e) => e.value === EventTypes.AvalancheOccurrence);
    let occurrence = createXnOccurrence(event);
    if (occurrence.internal.aval_cntrl_type_cd != null) {
      const group = groupTrigTypes(filterExplosiveTriggerTypes(occurrence.internal.aval_cntrl_type_cd))[0];
      occurrence.internal['aval_trig_type_cd'] = group.options.length > 0 ? group.options[0] : '';
    }
    AWPLocalDB.avalancheData
      .add(occurrence)
      .then(async () => {
        toast.success('Occurrence has been created');
        setEventId(occurrence.id);
        setEventTypeToActOn(eventType);
        setMode(FORM_MODE.EDIT);
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      });
  };

  const actionMenu = (o) => {
    if (!o) return '';
    let row = o.row;
    let rows = o.rows;
    let toggleRowExpanded = o.toggleRowExpanded;
    let isChild = o.isChild ? o.isChild : false;
    const record = o.row.original;
    if (!record) return '';
    let hasChildRows = false;
    if (record.ChildIngestionCandidates && record.ChildIngestionCandidates.length > 0) hasChildRows = true;
    const eventType = eventTypes.find((e) => e.value === record.event_type);
    let isXnOccurrence = false;
    //explosive control event occurrence
    if (record && record.internal && record.internal.aval_trig_type_cd && record.parent_trace_id) isXnOccurrence = true;
    return (
      <>
        <div>
          {(record.status === OfflineFormStatus.Valid ||
            record.status === OfflineFormStatus.Invalid ||
            record.status === OfflineFormStatus.Duplicated) && (
            <i
              className="fas fa-edit mx-1 my-1 awp-img-btn"
              title="Edit"
              onClick={() => {
                setMode(FORM_MODE.EDIT);
                setIsDuplicated(false);
                setEventId(record.id);
                setEventTypeToActOn(eventType);
              }}
            ></i>
          )}
          <i
            className="fas fa-eye mx-1 my-1 awp-img-btn"
            title="View"
            onClick={() => {
              setMode(FORM_MODE.VIEW);
              setEventId(record.id);
              setEventTypeToActOn(eventType);
            }}
          ></i>
          {!isXnOccurrence &&
            (() => {
              const { isValid, invalidLabels } = isValidToDuplicate(record);
              return isValid ? (
                <i
                  className="fas fa-copy mx-1 my-1 awp-img-btn"
                  title="Duplicate"
                  onClick={() => {
                    handleDuplicate(record);
                    setEventTypeToActOn(eventType);
                  }}
                />
              ) : (
                <i
                  className="fas fa-copy mx-1 my-1 awp-img-btn"
                  style={{ color: '#ccc' }}
                  title={`${invalidLabels.join('\n')}`}
                />
              );
            })()}
          {(record.status === OfflineFormStatus.Valid ||
            record.status === OfflineFormStatus.Invalid ||
            record.status === OfflineFormStatus.Duplicated) && (
            <i
              className="fas fa-trash mx-1 my-1 awp-img-btn"
              title="Delete"
              onClick={() => {
                checkChildOccrruences(record);
              }}
            ></i>
          )}
          {record.event_type === EventTypes.RoadClosure &&
            record.status !== OfflineFormStatus.Invalid &&
            record.status !== OfflineFormStatus.Duplicated && (
              <i
                className="fas fa-plus mx-1 my-1 awp-img-btn"
                title="Add Child"
                onClick={() => {
                  setIsDuplicated(false);
                  handleAddPhasedEvent(record, fullData);
                }}
              ></i>
            )}
          {record &&
            record.status !== formFailedStatus.IngestFailed &&
            record.status !== formFailedStatus.PushFailed &&
            record.event_type === EventTypes.AvalancheControl &&
            record.aval_is_avalanche_success === 'Y' && (
              <i
                className="fas fa-plus mx-1 my-1 awp-img-btn"
                title="Add Child"
                onClick={() => {
                  setIsDuplicated(false);
                  handleAddXnOccurrence(record);
                }}
              ></i>
            )}
          {!isChild && hasChildRows && (
            <>
              {!isValueEmpty(row) &&
                !isValueEmpty(rows) &&
                cellComponentExp({
                  row,
                  rows,
                  toggleRowExpanded,
                  isChild,
                  setExpandedRowEntityId,
                  setScrollPosition,
                  EntityId,
                })}
            </>
          )}
          <SupportOffline supportOffline={false}>
            {record.status === OfflineFormStatus.Valid && (
              <UploadWithCheckbox record={record} handleSubmit={handleSubmitMain} hasChildRows={hasChildRows} />
            )}
          </SupportOffline>
        </div>
      </>
    );
  };

  const getEventType = (row, breakpoints) => {
    if (isValueEmpty(row)) return '';
    if (!row.event_type) return '';
    const _value = row.internal;
    const _event_type = eventTypes?.find((x) => x.value === row.event_type);
    let ret = breakpoints.isLarge ? _event_type.label : _event_type.Abbreviation;
    if (row.event_type === EventTypes.AvalancheOccurrence || row.EventTypeNm === EventTypes.AvalancheControl) {
      const size = (_value?.aval_size_cd && _value?.aval_size_cd.value) || '-';
      let trig_type = '-';
      if (_value?.aval_trig_type_cd?.value.length > 0) {
        trig_type = _value?.aval_trig_type_cd?.value;
      } else {
        if (_value.aval_cntrl_type_cd != null) {
          const group = groupTrigTypes(filterExplosiveTriggerTypes(_value.aval_cntrl_type_cd))[0];
          _value['aval_trig_type_cd'] = group.options.length > 0 ? group.options[0] : '';
          trig_type = _value?.aval_trig_type_cd?.value;
        }
      }
      ret = (breakpoints.isLarge ? 'AvOb' : ret) + '/' + trig_type + '/' + size;
    }
    return ret;
  };
  const getResizedLocationName = (evt, breakpoints) => {
    let ret = getLocation(evt);
    if (evt.event_type === EventTypes.AvalancheOccurrence || evt.event_type === EventTypes.AvalancheControl) {
      if (ret) return getLocationPrefix(ret, breakpoints);
    } else if (evt.event_type === EventTypes.HazardForecast) {
      if (ret) return getLocationPrefix(ret, breakpoints);
    } else {
      if (!isValueEmpty(evt.GeographicLineId)) return getLocationPrefix(ret, breakpoints);
      if (!isValueEmpty(evt.road_closure_segment)) return getLocationPrefix(ret, breakpoints);
      if (ret) return resizePointLocationName(ret, breakpoints);
    }
    return ret;
  };

  const sortByCustomEventType = (rowA, rowB, id) => {
    const parseEventType = (eventType) => {
      if (!eventType) return null;
      const parts = eventType.split('/');
      return {
        part1: parts[0],
        part2: parts[1],
        part3: parseFloat(parts[2]) || 0,
      };
    };

    const compA = getEventType(rowA.original || rowA, breakpoints);
    const compB = getEventType(rowB.original || rowB, breakpoints);

    if (!compA || !compB) {
      return 'basic';
    }

    const evtA = parseEventType(compA);
    const evtB = parseEventType(compB);

    if (!evtA && evtB) return -1;
    if (evtA && !evtB) return 1;
    if (!evtA && !evtB) return 0;

    if (evtA.part1 !== evtB.part1) {
      return evtA.part1.toLowerCase().localeCompare(evtB.part1.toLowerCase());
    }

    if (evtA.part2 !== evtB.part2) {
      return evtA.part2.toLowerCase().localeCompare(evtB.part2.toLowerCase());
    }
    evtB.part3 = evtB.part3 == '-' ? 0 : evtB.part3;
    evtA.part3 = evtA.part3 == '-' ? 0 : evtA.part3;
    if (evtA.part3 !== evtB.part3) {
      return evtA.part3 >= evtB.part3 ? 1 : -1;
    }

    return 0;
  };

  const renderRowSubComponent = useCallback(
    ({ row, rows, toggleRowExpanded }) => {
      if (!row || !row.original) return '';
      if (!row.original.ChildIngestionCandidates || row.original.ChildIngestionCandidates.length === 0) return '';

      const sortedColumn = row.cells.find((cell) => cell.column.isSorted);

      if (sortedColumn) {
        const columnId = sortedColumn.column.id;
        const isDesc = sortedColumn.column.isSortedDesc;

        row.original.ChildIngestionCandidates.sort((a, b) => {
          let comparison = 0;
          switch (columnId) {
            case 'timestamp':
              comparison = new Date(a.timestamp) - new Date(b.timestamp);
              break;
            case 'event_type':
              comparison = sortByCustomEventType(a, b, 'event_type');
              break;
            case 'location':
              comparison = a.location.localeCompare(b.location);
              break;
            case 'status':
              comparison = a.status.localeCompare(b.status);
              break;
            case 'lastUpdated':
              comparison = new Date(a.lastUpdated) - new Date(b.lastUpdated);
              break;
            default:
              return 0;
          }
          return isDesc ? -comparison : comparison;
        });
      }
      let isChild = true;
      return (
        <>
          <tr style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
            {breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall ? (
              <td colSpan="7"></td>
            ) : (
              <td colSpan="5"></td>
            )}
          </tr>
          {row.original.ChildIngestionCandidates.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map(
            (childEvt, i) => {
              const evt = JSON.parse(childEvt.JsonObject);
              evt.internal.timestamp = new Date(evt.internal.timestamp);
              evt.ChildIngestionCandidates = childEvt.ChildIngestionCandidates;
              let evtTmp = getUtcToLocalByBreakpoints(evt.timestamp, breakpoints);
              let lastTmp = getUtcToLocalByBreakpoints(evt.lastUpdated, breakpoints);
              let cntlEvtName = getEventType(evt, breakpoints);
              let lv2Location = getResizedLocationName(evt, breakpoints);
              if (
                evt.event_type === EventTypes.AvalancheControl &&
                evt.ChildIngestionCandidates &&
                evt.ChildIngestionCandidates.length > 0
              ) {
                return (
                  <React.Fragment key={`frag-rcs-${evt.EventId}-${i}`}>
                    <tr key={`rcs-${evt.EventId}-${i}`} style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
                      <td style={{ width: '15%', borderTop: '0rem' }}>{evtTmp}</td>
                      <td style={{ width: '15%', borderTop: '0rem' }}>
                        <i
                          className="fa fa-arrow-right"
                          aria-hidden="true"
                          style={{ marginRight: '0.2rem', color: '#ffffff' }}
                        />
                        {cntlEvtName}
                      </td>
                      <td colSpan="2" style={{ width: '35%', borderTop: '0rem' }}>
                        {lv2Location}
                      </td>
                      {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                        <td style={{ width: '10%', borderTop: '0rem' }}>{evt.Status}</td>
                      )}
                      {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                        <td style={{ width: '15%', borderTop: '0rem' }}>{lastTmp}</td>
                      )}
                      <td style={{ width: '10%', borderTop: '0rem' }}>
                        {actionMenu({
                          row: { original: { ...evt } },
                          rows: row.original.ChildIngestionCandidates.map((x, id) => {
                            return { id, original: x };
                          }),
                          toggleRowExpanded,
                          isChild,
                        })}
                      </td>
                    </tr>
                    {evt.ChildIngestionCandidates.map((cntlEvt, j) => {
                      const cntl = JSON.parse(cntlEvt.JsonObject);
                      cntl.internal.timestamp = new Date(cntl.internal.timestamp);
                      cntl.ChildIngestionCandidates = cntlEvt.ChildIngestionCandidates;
                      let isGrandChild =
                        cntl.ChildIngestionCandidates && cntl.ChildIngestionCandidates.length > 0 ? true : false;
                      let cntlTmp = getUtcToLocalByBreakpoints(cntl.timestamp, breakpoints);
                      let cntllastTmp = getUtcToLocalByBreakpoints(cntl.lastUpdated, breakpoints);
                      let evtName = getEventType(cntl, breakpoints);
                      let lv3Location = getResizedLocationName(cntl, breakpoints);
                      return (
                        <tr
                          key={`rcs-cntl-${cntl.EventId}-${j}`}
                          style={{ backgroundColor: '#ffffff', color: '#666666' }}
                        >
                          <td style={{ width: '15%', borderTop: '0rem' }}>{cntlTmp}</td>
                          <td style={{ width: '15%', borderTop: '0rem' }}>
                            <i className="fa fa-arrow-right" aria-hidden="true" style={{ marginRight: '0.1rem' }} />
                            <i className="fa fa-arrow-right" aria-hidden="true" style={{ marginRight: '0.2rem' }} />
                            {evtName}
                          </td>
                          <td colSpan="1" style={{ width: '35%', borderTop: '0rem' }}>
                            {lv3Location}
                          </td>
                          {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                            <td style={{ width: '10%', borderTop: '0rem' }}>{cntl.status}</td>
                          )}
                          {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                            <td style={{ width: '15%', borderTop: '0rem' }}>{cntllastTmp}</td>
                          )}
                          <td style={{ width: '10%', borderTop: '0rem' }}>
                            {actionMenu({
                              row: { original: { ...cntl } },
                              rows:
                                cntl.ChildIngestionCandidates?.map((x, id) => {
                                  return { id, original: x };
                                }) ?? [],
                              toggleRowExpanded,
                              isGrandChild,
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              } else {
                return (
                  <tr key={`rcs-${evt.EventId}-${i}`} style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
                    <td style={{ width: '15%', borderTop: '0rem' }}>{evtTmp}</td>
                    <td style={{ width: '15%', borderTop: '0rem' }}>
                      {' '}
                      <i
                        className="fa fa-arrow-right"
                        aria-hidden="true"
                        style={{ marginRight: '0.2rem', color: '#ffffff' }}
                      />
                      {cntlEvtName}
                    </td>
                    <td colSpan="1" style={{ width: '35%', borderTop: '0rem' }}>
                      {lv2Location}
                    </td>
                    {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                      <td style={{ width: '10%', borderTop: '0rem' }}>{evt.status}</td>
                    )}
                    {(breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall) && (
                      <td style={{ width: '15%', borderTop: '0rem' }}>{lastTmp}</td>
                    )}
                    <td style={{ width: '10%', borderTop: '0rem' }}>
                      {actionMenu({
                        row: { original: { ...evt } },
                        rows: row.original.ChildIngestionCandidates.map((x, id) => {
                          return { id, original: x };
                        }),
                        toggleRowExpanded,
                        isChild,
                      })}
                    </td>
                  </tr>
                );
              }
            },
          )}
          <tr style={{ backgroundColor: '#aaaaaa', color: '#ffffff' }}>
            {breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall ? (
              <td colSpan="7" style={{ borderTop: '0rem' }}>
                <Container style={{ justifyContent: 'center', display: 'flex' }}>
                  {cellComponentExp({
                    row,
                    rows,
                    toggleRowExpanded,
                    isChild,
                    setExpandedRowEntityId,
                    setScrollPosition,
                    EntityId,
                  })}
                </Container>
              </td>
            ) : (
              <td colSpan="5" style={{ borderTop: '0rem' }}>
                <Container style={{ justifyContent: 'center', display: 'flex' }}>
                  {cellComponentExp({
                    row,
                    rows,
                    toggleRowExpanded,
                    isChild,
                    setExpandedRowEntityId,
                    setScrollPosition,
                    EntityId,
                  })}
                </Container>
              </td>
            )}
          </tr>
        </>
      );
    },
    [breakpoints],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Event Date/Time',
        accessor: 'timestamp',
        sortType: sortByNumericThenAlpha,
        Cell: ({ value }) => {
          return getUtcToLocalByBreakpoints(value, breakpoints);
        },
      },
      {
        Header: 'Event Type',
        accessor: 'event_type',
        sortType: sortByCustomEventType,
        Cell: (props) => {
          const r = props?.row?.original;
          return getEventType(r, breakpoints);
        },
      },
      {
        Header: 'Location',
        accessor: 'location',
        sortType: sortByNumericThenAlpha,
        Cell: (props) => {
          const row = props.row.original;
          const eventType = row.event_type;
          let ret = props.value;
          if (eventType === EventTypes.AvalancheOccurrence || eventType === EventTypes.AvalancheControl) {
            return getLocationPrefix(ret, breakpoints);
          } else {
            if (!isValueEmpty(row.road_closure_segment)) return getLocationPrefix(ret, breakpoints);
            if (ret) return resizePointLocationName(ret, breakpoints);
          }
          return ret;
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        sortType: sortByNumericThenAlpha,
        show: breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall,
      },
      {
        Header: breakpoints.isLarge || breakpoints.isMedium ? 'Last Updated' : 'Updated',
        accessor: 'lastUpdated',
        sortType: sortByNumericThenAlpha,
        show: breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall,
        Cell: ({ value }) => {
          return getUtcToLocalByBreakpoints(value, breakpoints);
        },
      },
      {
        Header: 'Actions',
        accessor: 'id',
        disableSortBy: true,
        Cell: (props) => actionMenu(props),
      },
    ],
    [breakpoints, fullData],
  );
  const eventTypesFiltered = eventTypes.filter(
    (x) => x.value != 'NOTIFICATION EVENT' && x.value != 'AVALANCHE FORECAST EVENT',
  );
  return (
    <>
      <div className="d-flex">
        <h1 className="flex-grow-1" style={{ marginBottom: '0px' }}>
          Avalanche Events List
        </h1>
      </div>
      <Accordion
        defaultActiveKey={isAccordionExpanded ? '0' : '1'}
        onSelect={(event) => {
          setScrollPosition(window.scrollY); // A change in the accordion will change the scroll position
          setIsAccordionExpanded(!!event);
        }}
      >
        <Card border="light" className="page-card">
          <CardHeaderToggle as="h3" eventKey="0" title={title} />
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <Row>
                <Col sm={5} md={6}>
                  <Row>
                    <Col md={6}>
                      <Form.Label className="mr-3" style={{ display: 'inlineBlock', width: '4em' }}>
                        <b>Start</b>
                      </Form.Label>
                      <DatePicker
                        showTime={{ isEnabled: false }}
                        dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        handleChange={(val) => {
                          setStartDate(dateBuilder(new Date(val), startDate, startDateValidator(endDate)));
                          setEndDate(dateBuilder(endDate.value, endDate, endDateValidator(startDate)));
                        }}
                        elementState={startDate}
                      />
                    </Col>
                  </Row>
                </Col>

                <Col sm={5} md={6}>
                  <Row>
                    <Col md={6}>
                      <Form.Label className="mr-3" style={{ display: 'inlineBlock', width: '4em' }}>
                        <b>End</b>
                      </Form.Label>
                      <DatePicker
                        showTime={{ isEnabled: false }}
                        dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        handleChange={(val) => {
                          setEndDate(dateBuilder(new Date(val), endDate, endDateValidator(startDate)));
                          setStartDate(dateBuilder(startDate.value, startDate, startDateValidator(endDate)));
                        }}
                        elementState={endDate}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedProgram}
                    onChange={setSelectedProgram}
                    data={{
                      options: avalanchePrograms,
                      status: 'succeeded',
                    }}
                    title="Program"
                  />
                </Col>
                <Col sm={5} md={6}>
                  <Form.Label>
                    <b>Search</b>
                  </Form.Label>
                  <input
                    className="form-control"
                    placeholder="Search..."
                    id="user_text_search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  ></input>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedArea}
                    onChange={setSelectedArea}
                    data={{
                      options: getFilteredArea(),
                      status: 'succeeded',
                    }}
                    title="Area"
                  />
                </Col>
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedStatus}
                    onChange={setSelectedStatus}
                    data={internalIngestionCandidateStatus}
                    title="Status"
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedEventType}
                    onChange={setSelectedEventType}
                    data={{
                      options: eventTypesFiltered,
                      status: 'succeeded',
                    }}
                    title="Event"
                  />
                </Col>
                <Col sm={5} md={6}>
                  <AwpDropdown
                    isMulti={true}
                    selectedValues={selectedAvalanchTech}
                    onChange={setSelectedAvalancheTech}
                    data={avalancheTechs}
                    title="Submitted By"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>

      <Table
        id="event_table"
        columns={columns}
        data={getFilteredData()}
        fetchData={() => {}}
        loading={loading}
        search={[]}
        filters={[]}
        dataTriggers={[]}
        defaultSortBy="timestamp"
        index={index}
        setIndex={setIndex}
        sort={sort}
        setSort={setSort}
        defaultPageSize={defaultPageSize}
        renderRowSubComponent={renderRowSubComponent}
        setDefaultPageSize={setDefaultPageSize}
        treeExpand={true}
        tdRowStyle={{ padding: '0rem' }}
        thRowStyle={{ padding: '0rem' }}
        scrollPosition={scrollPosition}
        setScrollPosition={setScrollPosition}
        tableClasses="condensed-rows" //use class to remove top/bottom padding
        simpleBar={false} //toggle table type between simplebar or standard table
        maxHeight={0} //maxHeight set to zero results in full page table
      />
      <div className="my-3">
        <Row>
          <Col sm={3} md={4}>
            <AwpDropdown
              isMulti={false}
              selectedValues={eventTypeToCreate}
              onChange={setEventTypeToCreate}
              data={{
                options: parentEventTypes,
                status: 'succeeded',
              }}
              title="Select Event"
              allowAll={false}
              placeholder="Select Event"
            />
          </Col>
        </Row>
        <Row>
          <Col sm={3} md={4}>
            <Button
              className="mr-2 my-3"
              variant="primary"
              onClick={() => {
                setMode(FORM_MODE.CREATE);
              }}
              disabled={loading}
            >
              Create
            </Button>
          </Col>
        </Row>
      </div>
      <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
      <AwpYesNoModal
        showYesNoModal={showYesNoModal}
        setShowYesNoModal={setShowYesNoModal}
        onClickYes={handleDelete}
        title="Delete"
        body="Are you sure you want to delete?"
      />
    </>
  );
};

EventTable.propTypes = {
  title: PropTypes.string,
  breakpoints: PropTypes.object,
  setFullData: PropTypes.func,
  fullData: PropTypes.array,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setMode: PropTypes.func,
  eventId: PropTypes.string,
  setEventId: PropTypes.func,
  startDate: PropTypes.object,
  setStartDate: PropTypes.func,
  endDate: PropTypes.object,
  setEndDate: PropTypes.func,
  selectedAvalanchTech: PropTypes.array,
  setSelectedAvalancheTech: PropTypes.func,
  selectedProgram: PropTypes.array,
  setSelectedProgram: PropTypes.func,
  selectedArea: PropTypes.array,
  setSelectedArea: PropTypes.func,
  selectedStatus: PropTypes.array,
  setSelectedStatus: PropTypes.func,
  selectedEventType: PropTypes.array,
  setSelectedEventType: PropTypes.func,
  search: PropTypes.string,
  setSearch: PropTypes.func,
  setEventTypeToActOn: PropTypes.func,
  setEventTypeToCreate: PropTypes.func,
  eventTypeToCreate: PropTypes.object,
  index: PropTypes.number,
  setIndex: PropTypes.func,
  sort: PropTypes.any,
  setSort: PropTypes.func,
  defaultPageSize: PropTypes.number,
  setDefaultPageSize: PropTypes.func,
  setIsDuplicated: PropTypes.func,
};

export default EventTable;
